<template>
  <div class="min-h-screen lg:flex">
    <Sidebar />
    <GlobalNavbar />
    <main class="min-w-0 flex-1 px-4 pb-8 pt-24 lg:ml-64 lg:px-8">
      <!-- Hero Section -->
      <div class="mb-12">
        <p class="mb-3 text-sm font-semibold text-blue-600">StackLab Docs</p>
        <h1 class="text-3xl font-bold tracking-tight text-zinc-950 sm:text-4xl">Documentação das soluções digitais da StackLab</h1>
        <p class="mt-4 text-lg leading-8 text-zinc-600">
          Encontre guias de referência, endpoints e exemplos para integrar o Kanban para Chatwoot e a StackZap API aos seus produtos.
        </p>
      </div>

      <section class="mb-12 grid grid-cols-1 gap-5 md:grid-cols-2" aria-labelledby="api-docs-title">
        <h2 id="api-docs-title" class="sr-only">Documentações de API</h2>
        <div
          class="relative overflow-hidden rounded-xl border border-zinc-200 bg-white p-6"
        >
          <div class="absolute -right-10 -top-10 h-28 w-28 rounded-full bg-blue-50 transition group-hover:scale-125"></div>
          <div class="relative">
            <div class="mb-8 flex items-center justify-between">
              <div class="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                <BookOpen weight="duotone" class="h-5 w-5" aria-hidden="true" />
              </div>
            </div>
            <p class="text-xs font-semibold uppercase tracking-wide text-blue-600">Documentação da API</p>
            <h3 class="mt-2 text-xl font-semibold text-zinc-950"><Link href="/kanban" class="hover:text-blue-700">Kanban para Chatwoot</Link></h3>
            <p class="mt-2 text-sm leading-6 text-zinc-600">Funnels, itens do Kanban, estágios, agentes e métricas.</p>
            <div class="mt-5 flex flex-wrap gap-2" aria-label="Principais endpoints do Kanban">
              <Link v-for="endpoint in apiHighlights.kanban" :key="endpoint.path" :href="`/kanban#${endpoint.anchor}`" class="inline-flex items-center gap-1.5 rounded-md bg-zinc-50 px-2 py-1 text-[11px] font-medium text-zinc-600 transition hover:bg-blue-50 hover:text-blue-700">
                <span class="font-semibold text-blue-600">{{ endpoint.method }}</span>{{ endpoint.label }}
              </Link>
            </div>
          </div>
        </div>

        <div
          class="relative overflow-hidden rounded-xl border border-zinc-200 bg-white p-6"
        >
          <div class="absolute -right-10 -top-10 h-28 w-28 rounded-full bg-emerald-50 transition group-hover:scale-125"></div>
          <div class="relative">
            <div class="mb-8 flex items-center justify-between">
              <div class="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                <Lightning weight="duotone" class="h-5 w-5" aria-hidden="true" />
              </div>
            </div>
            <p class="text-xs font-semibold uppercase tracking-wide text-emerald-600">Documentação da API</p>
            <h3 class="mt-2 text-xl font-semibold text-zinc-950"><Link href="/stackzap" class="hover:text-emerald-700">StackZap API</Link></h3>
            <p class="mt-2 text-sm leading-6 text-zinc-600">Instâncias WhatsApp, mensagens, webhooks e integrações.</p>
            <div class="mt-5 flex flex-wrap gap-2" aria-label="Principais endpoints da StackZap">
              <Link v-for="endpoint in apiHighlights.stackzap" :key="endpoint.path" :href="`/stackzap#${endpoint.anchor}`" class="inline-flex items-center gap-1.5 rounded-md bg-zinc-50 px-2 py-1 text-[11px] font-medium text-zinc-600 transition hover:bg-emerald-50 hover:text-emerald-700">
                <span class="font-semibold text-emerald-600">{{ endpoint.method }}</span>{{ endpoint.label }}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section aria-labelledby="manuals-title">
        <div class="mb-5">
          <p class="text-xs font-semibold uppercase tracking-wide text-amber-600">Guias práticos</p>
          <h2 id="manuals-title" class="mt-2 text-2xl font-semibold tracking-tight text-zinc-950">Manuais</h2>
          <p class="mt-2 max-w-2xl text-sm leading-6 text-zinc-600">Tutoriais para instalar, configurar e aproveitar melhor as soluções da StackLab.</p>
        </div>
        <Link href="/manuais" class="relative block overflow-hidden rounded-xl border border-zinc-200 bg-white p-6">
          <div class="absolute -right-10 -top-10 h-28 w-28 rounded-full bg-amber-50"></div>
          <div class="relative flex items-start gap-4">
            <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
              <BookOpen weight="duotone" class="h-5 w-5" aria-hidden="true" />
            </div>
            <div>
              <h3 class="text-xl font-semibold text-zinc-950">Todos os manuais</h3>
              <p class="mt-2 max-w-2xl text-sm leading-6 text-zinc-600">Encontre instruções para o Kanban para Chatwoot e a StackZap, organizadas por solução e plataforma.</p>
            </div>
          </div>
        </Link>
        <div class="mt-4 flex flex-wrap gap-2" aria-label="Categorias de manuais">
          <Link v-for="category in manualCategories" :key="category.slug" :href="`/manuais/categoria/${category.slug}`" class="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-600 transition hover:border-zinc-400 hover:text-zinc-950">
            {{ category.name }}
            <span class="rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold text-zinc-400">{{ categoryCount(category.slug) }}</span>
          </Link>
        </div>
      </section>

    </main>
  </div>
</template>

<script setup lang="ts">
import { Link } from "@inertiajs/vue3";
import { PhBookOpen as BookOpen, PhLightning as Lightning } from "@phosphor-icons/vue";
import Sidebar from "../components/layout/Sidebar.vue";
import GlobalNavbar from "../components/layout/GlobalNavbar.vue";
import { getManualCategory, manualCategories, manualCatalog } from "../data/manualCatalog";

const apiHighlights = {
  kanban: [
    { method: "GET", label: "Listar funis", path: "/accounts/{account_id}/funnels", anchor: "get-accounts-account-id-funnels" },
    { method: "POST", label: "Criar item", path: "/accounts/{account_id}/kanban_items", anchor: "post-accounts-account-id-kanban-items" },
    { method: "POST", label: "Mover item", path: "/accounts/{account_id}/kanban_items/{id}/move_to_stage", anchor: "post-accounts-account-id-kanban-items-id-move-to-stage" },
  ],
  stackzap: [
    { method: "POST", label: "Criar instância", path: "/v1/instances", anchor: "post-v1-instances" },
    { method: "POST", label: "Enviar texto", path: "/v1/instances/{id}/messages/text", anchor: "post-v1-instances-id-messages-text" },
    { method: "POST", label: "Criar webhook", path: "/v1/webhooks", anchor: "post-v1-webhooks" },
  ],
};
const categoryCount = (slug: string) => manualCatalog.reduce((total, solution) => total + solution.manuals.filter((manual) => getManualCategory(manual) === slug).length, 0);
</script>
